import { Outlet, createFileRoute, redirect, useLocation } from '@tanstack/react-router';
import { getLoginURL } from 'utils';
import { resolveAppMode } from 'utils/appMode';
import { getUserSession } from 'utils/token';

import { profileQueryOptions } from 'api/account';

import { useRoleGuard } from 'component/hooks/useRoleGuard';
import { Page } from 'component/layout/Page';
import { SimplePage } from 'component/layout/SimplePage';
import { NoAccountsHeader } from 'component/partial/NoAccountsHeader';
import { SiteUserHeader } from 'component/partial/SiteUserHeader';
import { useAppMode } from 'component/root/AppModeProvider';
import { BackgroundTaskProvider } from 'component/root/BackgroundTaskProvider';

import { requirePasswordSet } from './-guards';

export const Route = createFileRoute('/n/_auth')({
  beforeLoad: async ({ context, location }) => {
    const appMode = resolveAppMode(location.searchStr);
    const isRegistrationRoute =
      location.pathname.startsWith('/n/register/plan') ||
      location.pathname.startsWith('/n/register/success');
    if (isRegistrationRoute && appMode.isWpSiteControl) {
      throw redirect({
        to: '/n/not-found',
        replace: true,
      });
    }

    const session = getUserSession();

    const sdkPassed = appMode.isSdk && appMode.hasSdkQueryParam;

    if (!session && !sdkPassed) {
      const loginURL = appMode.isWpSiteControl
        ? appMode.loginUrl
        : getLoginURL({
            pathname: location.pathname,
            search: location.searchStr,
          });
      window.location.href = loginURL;
      throw redirect({
        to: appMode.isWpSiteControl ? '/login/wpsitecontrol' : '/login',
        replace: true,
      });
    }

    await context.ensureQueryData(profileQueryOptions());

    await requirePasswordSet({ context });

    return { session };
  },
  component: AuthLayout,
});

function AuthLayout() {
  const location = useLocation();
  const { isWpSiteControl } = useAppMode();

  const isRegistrationRoute =
    location.pathname.startsWith('/n/register/plan') ||
    location.pathname.startsWith('/n/register/success');

  const isManageRoute = location.pathname.startsWith('/n/manage/');

  const isNoAccountsUser = useRoleGuard(['user_with_no_accounts'], 'allow');

  if (isRegistrationRoute || isManageRoute) {
    return <Outlet />;
  }

  if (isNoAccountsUser) {
    return (
      <BackgroundTaskProvider>
        <SimplePage header={<NoAccountsHeader />}>
          <Outlet />
        </SimplePage>
      </BackgroundTaskProvider>
    );
  }

  if (isWpSiteControl) {
    return (
      <BackgroundTaskProvider>
        <SimplePage header={<SiteUserHeader />}>
          <Outlet />
        </SimplePage>
      </BackgroundTaskProvider>
    );
  }

  return (
    <BackgroundTaskProvider>
      <Page>
        <Outlet />
      </Page>
    </BackgroundTaskProvider>
  );
}
